import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getVendors, updateVendor } from "../../services/api/boq/VendorService";
import { ArrowLeft } from "lucide-react";

export default function EditVendor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    gst_number: "",
    address: "",
    status: 1,
  });

  const [errors, setErrors] = useState({});

  // ======================
  // Fetch vendor
  // ======================
  const fetchVendor = async () => {
    try {
      const res = await getVendors();
      let list = [];

      if (Array.isArray(res.data?.data)) list = res.data.data;
      else if (Array.isArray(res.data?.data?.data))
        list = res.data.data.data;

      const vendor = list.find((v) => String(v.id) === String(id));

      if (!vendor) {
        toast.error("Vendor not found");
        navigate("/vendors");
        return;
      }

      setForm({
        name: vendor.name ?? "",
        mobile: vendor.mobile ?? "",
        email: vendor.email ?? "",
        gst_number: vendor.gst_number ?? "",
        address: vendor.address ?? "",
        status: vendor.status === "active" || vendor.status === 1 ? 1 : 0,
      });
    } catch (err) {
      toast.error("Failed to load vendor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, []);

  // ======================
  // Validate
  // ======================
  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";

    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email))
      newErrors.email = "Invalid email format";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ======================
  // Submit
  // ======================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix errors");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name,
        mobile: form.mobile || null,
        email: form.email,
        gst_number: form.gst_number || null,
        address: form.address || null,
        status: Number(form.status),
      };

      await updateVendor(id, payload);

      toast.success("Vendor updated successfully");

      navigate("/vendors");
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500";

  if (loading)
    return <div className="p-6 text-center text-gray-500">Loading...</div>;

  return (
    <div className="p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/vendors")}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-black"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        <h2 className="text-xl font-semibold">Edit Vendor</h2>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6 max-w-full">

        <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-6">

          {/* Name */}
          <div>
            <label className="text-sm font-medium">Vendor Name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium">Email</label>
            <input
              className={inputClass}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="text-sm font-medium">Mobile</label>
            <input
              className={inputClass}
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            />
          </div>

          {/* GST */}
          <div>
            <label className="text-sm font-medium">GST Number</label>
            <input
              className={inputClass}
              value={form.gst_number}
              onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
            />
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-medium">Status</label>
            <select
              className={inputClass}
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: Number(e.target.value) })
              }
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Address</label>
            <textarea
              className={inputClass}
              rows="3"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          {/* Buttons */}
          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t">

            <button
              type="button"
              onClick={() => navigate("/vendors")}
              className="px-4 py-2 border rounded-lg"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
            >
              {saving && (
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {saving ? "Updating..." : "Update Vendor"}
            </button>

          </div>
        </form>

      </div>
    </div>
  );
}